<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y" v-if="classroom">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ classroom.name }}</h4>
					<p class="mb-0 text-muted" v-if="classroom.description">{{ classroom.description }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-3">
					<button type="button" class="btn btn-outline-secondary" @click="$router.push('/virtual-classrooms')">
						<i class="ri-arrow-left-line me-1"></i>{{ $t('common.actions.back') }}
					</button>
					<button type="button" class="btn btn-primary" @click="$router.push(`/virtual-classrooms/edit/${classroom._id}`)">
						<i class="ri-pencil-line me-1"></i>{{ $t('common.actions.edit') }}
					</button>
				</div>
			</div>

			<!-- Tabs -->
			<ul class="nav nav-tabs mb-6">
				<li class="nav-item">
					<a class="nav-link" :class="{ active: activeTab === 'overview' }" href="javascript:void(0);" @click="activeTab = 'overview'">
						<i class="ri-eye-line me-1"></i>{{ $t('virtualClassrooms.detail.overviewTab') }}
					</a>
				</li>
				<li class="nav-item">
					<a class="nav-link" :class="{ active: activeTab === 'participants' }" href="javascript:void(0);" @click="activeTab = 'participants'">
						<i class="ri-group-line me-1"></i>{{ $t('virtualClassrooms.detail.participantsTab') }} ({{ participants.length }})
					</a>
				</li>
				<li class="nav-item">
					<a class="nav-link" :class="{ active: activeTab === 'attendance' }" href="javascript:void(0);" @click="openAttendanceTab">
						<i class="ri-checkbox-circle-line me-1"></i>{{ $t('virtualClassrooms.detail.attendanceTab') }}
					</a>
				</li>
				<li class="nav-item">
					<a class="nav-link" :class="{ active: activeTab === 'results' }" href="javascript:void(0);" @click="activeTab = 'results'">
						<i class="ri-bar-chart-2-line me-1"></i>{{ $t('virtualClassrooms.detail.resultsTab') }}
					</a>
				</li>
			</ul>

			<!-- ═══════════ OVERVIEW TAB ═══════════ -->
			<div v-if="activeTab === 'overview'">
				<!-- Meeting link -->
				<div class="card mb-6">
					<div class="card-body">
						<div class="d-flex align-items-center flex-wrap gap-4">
							<div class="avatar">
								<span class="avatar-initial rounded-circle bg-label-info">
									<i class="ri-vidicon-line"></i>
								</span>
							</div>
							<div class="flex-grow-1">
								<h6 class="mb-1">{{ $t('virtualClassrooms.detail.meetingLink') }} — {{ $t(`virtualClassrooms.providers.${classroom.meetingProvider}`) }}</h6>
								<small class="text-muted text-break">{{ classroom.meetingUrl }}</small>
							</div>
							<div class="d-flex gap-3">
								<button type="button" class="btn btn-sm btn-outline-primary" @click="copyToClipboard(classroom.meetingUrl, 'meeting')">
									<i class="me-1" :class="copiedKey === 'meeting' ? 'ri-check-line' : 'ri-file-copy-line'"></i>
									{{ copiedKey === 'meeting' ? $t('virtualClassrooms.detail.linkCopied') : $t('virtualClassrooms.detail.copyLink') }}
								</button>
								<a :href="classroom.meetingUrl" target="_blank" rel="noopener" class="btn btn-sm btn-primary">
									<i class="ri-external-link-line me-1"></i>{{ $t('virtualClassrooms.detail.openMeeting') }}
								</a>
							</div>
						</div>
					</div>
				</div>

				<!-- Days -->
				<div class="card mb-6">
					<div class="card-header">
						<h5 class="mb-0">{{ $t('virtualClassrooms.detail.daysSection') }}</h5>
					</div>
					<div class="table-responsive">
						<table class="table table-borderless">
							<thead>
								<tr>
									<th>{{ $t('virtualClassrooms.form.dayLabel') }}</th>
									<th>{{ $t('virtualClassrooms.form.dateLabel') }}</th>
									<th>{{ $t('virtualClassrooms.form.startTimeLabel') }}</th>
									<th>{{ $t('virtualClassrooms.form.endTimeLabel') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(day, index) in classroom.days" :key="day.dayId" :class="{ 'table-active': isToday(day.date) }">
									<td><span class="badge bg-label-primary rounded-pill">{{ index + 1 }}</span></td>
									<td class="fw-medium">{{ formatDate(day.date) }}</td>
									<td>{{ day.startTime }}</td>
									<td>{{ day.endTime }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<!-- Chapters -->
				<div class="card">
					<div class="card-header">
						<h5 class="mb-0">{{ $t('virtualClassrooms.detail.chaptersSection') }}</h5>
					</div>
					<div class="table-responsive">
						<table class="table table-borderless">
							<thead>
								<tr>
									<th>#</th>
									<th>{{ $t('virtualClassrooms.form.chapterNameLabel') }}</th>
									<th>{{ $t('virtualClassrooms.form.dayLabel') }}</th>
									<th>{{ $t('microLearning.moduleForm.questionsSection') }}</th>
									<th>{{ $t('virtualClassrooms.participants.status') }}</th>
									<th class="text-end">{{ $t('virtualClassrooms.participants.actions') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(chapter, index) in classroom.chapters" :key="chapter.chapterId">
									<td><span class="badge bg-label-primary rounded-pill">{{ index + 1 }}</span></td>
									<td class="fw-medium">{{ chapter.name }}</td>
									<td>
										<span class="badge bg-label-info" v-if="chapterDayLabel(chapter)">{{ chapterDayLabel(chapter) }}</span>
										<span class="text-muted" v-else>—</span>
									</td>
									<td>{{ $t('virtualClassrooms.form.questionCount', { count: chapter.questions.length }) }}</td>
									<td>
										<span class="badge" :class="chapter.isUnlocked ? 'bg-label-success' : 'bg-label-secondary'">
											<i class="me-1" :class="chapter.isUnlocked ? 'ri-lock-unlock-line' : 'ri-lock-line'"></i>
											{{ chapter.isUnlocked ? $t('virtualClassrooms.detail.unlocked') : $t('virtualClassrooms.detail.locked') }}
										</span>
									</td>
									<td class="text-end">
										<button
											type="button"
											class="btn btn-sm"
											:class="chapter.isUnlocked ? 'btn-outline-secondary' : 'btn-primary'"
											@click="toggleChapterLock(chapter)"
											:disabled="isTogglingLock"
										>
											<i class="me-1" :class="chapter.isUnlocked ? 'ri-lock-line' : 'ri-lock-unlock-line'"></i>
											{{ chapter.isUnlocked ? $t('virtualClassrooms.detail.lock') : $t('virtualClassrooms.detail.unlock') }}
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- ═══════════ PARTICIPANTS TAB ═══════════ -->
			<div v-if="activeTab === 'participants'">
				<div class="card">
					<div class="card-header d-flex justify-content-between align-items-center">
						<h5 class="mb-0">{{ $t('virtualClassrooms.detail.participantsTab') }}</h5>
						<button type="button" class="btn btn-sm btn-primary" @click="openAssignModal">
							<i class="ri-user-add-line me-1"></i>{{ $t('virtualClassrooms.participants.addButton') }}
						</button>
					</div>
					<div class="table-responsive" v-if="participants.length > 0">
						<table class="table table-borderless">
							<thead>
								<tr>
									<th>{{ $t('virtualClassrooms.participants.name') }}</th>
									<th>{{ $t('virtualClassrooms.participants.status') }}</th>
									<th class="text-end">{{ $t('virtualClassrooms.participants.actions') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="participant in participants" :key="participant._id">
									<td>
										<div class="d-flex align-items-center">
											<div class="avatar avatar-sm me-3">
												<span class="avatar-initial rounded-circle bg-label-primary">{{ initials(participant.user) }}</span>
											</div>
											<div>
												<span class="fw-medium">{{ participant.user.firstName }} {{ participant.user.lastName }}</span>
												<small class="text-muted d-block">{{ participant.user.email }}</small>
											</div>
										</div>
									</td>
									<td>
										<span class="badge" :class="participant.status === 'started' ? 'bg-label-success' : 'bg-label-primary'">
											{{ $t(`virtualClassrooms.participants.${participant.status}`) }}
										</span>
									</td>
									<td class="text-end">
										<button type="button" class="btn btn-sm btn-icon btn-text-secondary" @click="copyToClipboard(participant.link, participant._id)" data-bs-toggle="tooltip" :data-bs-title="$t('virtualClassrooms.participants.copyLink')">
											<i :class="copiedKey === participant._id ? 'ri-check-line text-success' : 'ri-file-copy-line'"></i>
										</button>
										<button type="button" class="btn btn-sm btn-icon btn-text-secondary" @click="resendInvite(participant)" data-bs-toggle="tooltip" :data-bs-title="$t('virtualClassrooms.participants.resendInvite')">
											<i :class="invitedKey === participant._id ? 'ri-check-line text-success' : 'ri-mail-send-line'"></i>
										</button>
										<button type="button" class="btn btn-sm btn-icon btn-text-danger" @click="removeParticipant(participant)" data-bs-toggle="tooltip" :data-bs-title="$t('virtualClassrooms.participants.remove')">
											<i class="ri-delete-bin-6-line"></i>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="card-body text-center py-6" v-else>
						<i class="ri-group-line ri-48px text-muted mb-3 d-block"></i>
						<p class="text-muted mb-0">{{ $t('virtualClassrooms.participants.empty') }}</p>
					</div>
				</div>
			</div>

			<!-- ═══════════ ATTENDANCE TAB ═══════════ -->
			<div v-if="activeTab === 'attendance'">
				<div class="card">
					<div class="card-header">
						<h5 class="mb-4">{{ $t('virtualClassrooms.detail.attendanceTab') }}</h5>
						<div class="d-flex flex-wrap gap-2">
							<button
								v-for="(day, index) in classroom.days"
								:key="day.dayId"
								type="button"
								class="btn btn-sm"
								:class="selectedDayId === day.dayId ? 'btn-primary' : 'btn-outline-secondary'"
								@click="selectAttendanceDay(day.dayId)"
							>
								{{ $t('virtualClassrooms.form.dayLabel') }} {{ index + 1 }} — {{ formatDate(day.date) }}
							</button>
						</div>
					</div>
					<div class="table-responsive" v-if="participants.length > 0">
						<table class="table table-borderless">
							<thead>
								<tr>
									<th>{{ $t('virtualClassrooms.participants.name') }}</th>
									<th class="text-end">{{ $t('virtualClassrooms.attendance.selectDay') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="participant in participants" :key="participant._id">
									<td>
										<div class="d-flex align-items-center">
											<div class="avatar avatar-sm me-3">
												<span class="avatar-initial rounded-circle bg-label-primary">{{ initials(participant.user) }}</span>
											</div>
											<span class="fw-medium">{{ participant.user.firstName }} {{ participant.user.lastName }}</span>
										</div>
									</td>
									<td class="text-end">
										<div class="btn-group" role="group">
											<button
												type="button"
												class="btn btn-sm"
												:class="attendanceDraft[participant._id] === true ? 'btn-success' : 'btn-outline-success'"
												@click="setAttendance(participant._id, true)"
											>
												<i class="ri-check-line me-1"></i>{{ $t('virtualClassrooms.attendance.present') }}
											</button>
											<button
												type="button"
												class="btn btn-sm"
												:class="attendanceDraft[participant._id] === false ? 'btn-danger' : 'btn-outline-danger'"
												@click="setAttendance(participant._id, false)"
											>
												<i class="ri-close-line me-1"></i>{{ $t('virtualClassrooms.attendance.absent') }}
											</button>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
						<div class="card-body d-flex justify-content-end align-items-center gap-4">
							<span class="text-success small" v-if="attendanceSaved"><i class="ri-check-line me-1"></i>{{ $t('virtualClassrooms.attendance.saved') }}</span>
							<button type="button" class="btn btn-primary" @click="saveAttendance" :disabled="isSavingAttendance || markedCount === 0">
								<i class="ri-save-line me-1"></i>{{ $t('virtualClassrooms.attendance.save') }}
							</button>
						</div>
					</div>
					<div class="card-body text-center py-6" v-else>
						<i class="ri-group-line ri-48px text-muted mb-3 d-block"></i>
						<p class="text-muted mb-0">{{ $t('virtualClassrooms.attendance.empty') }}</p>
					</div>
				</div>
			</div>

			<!-- ═══════════ RESULTS TAB ═══════════ -->
			<div v-if="activeTab === 'results'">
				<div class="card">
					<div class="table-responsive" v-if="participants.length > 0">
						<table class="table table-borderless">
							<thead>
								<tr>
									<th>{{ $t('virtualClassrooms.participants.name') }}</th>
									<th v-for="(chapter, index) in classroom.chapters" :key="chapter.chapterId" class="text-center">
										<span data-bs-toggle="tooltip" :data-bs-title="chapter.name">{{ index + 1 }}. {{ truncate(chapter.name, 20) }}</span>
									</th>
									<th class="text-end"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="participant in participants" :key="participant._id">
									<td>
										<div class="d-flex align-items-center">
											<div class="avatar avatar-sm me-3">
												<span class="avatar-initial rounded-circle bg-label-primary">{{ initials(participant.user) }}</span>
											</div>
											<span class="fw-medium">{{ participant.user.firstName }} {{ participant.user.lastName }}</span>
										</div>
									</td>
									<td v-for="chapter in classroom.chapters" :key="chapter.chapterId" class="text-center">
										<span class="badge" :class="progressBadgeClass(participant, chapter.chapterId)">
											{{ progressLabel(participant, chapter.chapterId) }}
										</span>
									</td>
									<td class="text-end">
										<button type="button" class="btn btn-sm btn-icon btn-text-secondary" @click="openAnswersModal(participant)" data-bs-toggle="tooltip" :data-bs-title="$t('virtualClassrooms.results.viewAnswers')">
											<i class="ri-eye-line"></i>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="card-body text-center py-6" v-else>
						<i class="ri-bar-chart-2-line ri-48px text-muted mb-3 d-block"></i>
						<p class="text-muted mb-0">{{ $t('virtualClassrooms.results.empty') }}</p>
					</div>
				</div>
			</div>

		</div>

		<!-- ASSIGN MODAL -->
		<div class="modal fade" id="addParticipantsModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple">
				<div class="modal-content">
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>

					<div class="modal-body p-0">
						<div class="text-center mb-6">
							<h4 class="mb-2">{{ $t('virtualClassrooms.participants.addButton') }}</h4>
						</div>

						<div class="mb-4">
							<div class="form-floating form-floating-outline">
								<input
									type="text"
									class="form-control"
									:placeholder="$t('virtualClassrooms.participants.searchEmployees')"
									v-model="employeeSearch"
									@input="searchEmployeesDebounced"
									autocomplete="off"
								/>
								<label>{{ $t('virtualClassrooms.participants.searchEmployees') }}</label>
							</div>
						</div>

						<div class="mb-4" v-if="selectedEmployeeIds.length > 0">
							<small class="text-muted">{{ $t('virtualClassrooms.participants.selectedCount', { count: selectedEmployeeIds.length }) }}</small>
						</div>

						<div style="max-height: 300px; overflow-y: auto;">
							<div v-for="employee in availableEmployees" :key="employee._id" class="form-check mb-3">
								<input
									class="form-check-input"
									type="checkbox"
									:id="'emp-' + employee._id"
									:value="employee._id"
									v-model="selectedEmployeeIds"
									:disabled="alreadyAssignedIds.includes(employee._id)"
								/>
								<label class="form-check-label" :for="'emp-' + employee._id" :class="{ 'text-muted': alreadyAssignedIds.includes(employee._id) }">
									{{ employee.firstName }} {{ employee.lastName }}
									<small class="text-muted d-block">{{ employee.email }}</small>
									<span class="badge bg-label-secondary" v-if="alreadyAssignedIds.includes(employee._id)">{{ $t('virtualClassrooms.participants.alreadyAssigned') }}</span>
								</label>
							</div>
							<div v-if="availableEmployees.length === 0" class="text-center text-muted py-3">
								{{ $t('virtualClassrooms.participants.noEmployees') }}
							</div>
						</div>

						<div class="col-12 d-flex flex-wrap justify-content-center gap-4 row-gap-4 mt-5">
							<button type="button" class="btn btn-primary" @click="addParticipants()" :disabled="selectedEmployeeIds.length === 0 || isAddingParticipants">
								{{ $t('virtualClassrooms.participants.assign') }}
							</button>
							<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
								{{ $t('common.actions.cancel') }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ANSWERS MODAL -->
		<div class="modal fade" id="participantAnswersModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" v-if="answersParticipant">{{ $t('virtualClassrooms.results.answersTitle', { name: `${answersParticipant.user.firstName} ${answersParticipant.user.lastName}` }) }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>

					<div class="modal-body">
						<div v-if="participantAnswers.length === 0" class="text-center text-muted py-4">
							{{ $t('virtualClassrooms.results.noAnswers') }}
						</div>

						<div v-for="group in groupedAnswers" :key="group.chapterId" class="mb-6">
							<h6 class="mb-4"><i class="ri-book-open-line me-2"></i>{{ group.chapterName }}</h6>

							<div v-for="answer in group.answers" :key="answer._id" class="border rounded p-4 mb-3" :class="answer.answered ? (answer.isCorrect ? 'border-success' : 'border-danger') : ''">
								<div class="d-flex align-items-start justify-content-between gap-3">
									<p class="fw-medium mb-2">{{ answer.question }}</p>
									<span class="badge flex-shrink-0" :class="!answer.answered ? 'bg-label-secondary' : answer.isCorrect ? 'bg-label-success' : 'bg-label-danger'">
										{{ !answer.answered ? $t('virtualClassrooms.results.pending') : answer.isCorrect ? $t('virtualClassrooms.results.correct') : $t('virtualClassrooms.results.incorrect') }}
									</span>
								</div>
								<div v-if="answer.answered">
									<small class="d-block" :class="answer.isCorrect ? 'text-success' : 'text-danger'">
										{{ $t('virtualClassrooms.results.userReplies') }} : {{ answer.userReplies.join(', ') }}
									</small>
									<small class="d-block text-success" v-if="!answer.isCorrect">
										{{ $t('virtualClassrooms.results.validAnswers') }} : {{ answer.validAnswers.join(', ') }}
									</small>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";

export default {
	name: "VirtualClassroomDetail",

	data() {
		return {
			classroom: null,
			participants: [],
			activeTab: "overview",
			copiedKey: null,
			invitedKey: null,
			isTogglingLock: false,

			// Assign modal
			availableEmployees: [],
			selectedEmployeeIds: [],
			employeeSearch: "",
			searchTimeout: null,
			isAddingParticipants: false,

			// Attendance
			selectedDayId: null,
			attendanceDraft: {},
			isSavingAttendance: false,
			attendanceSaved: false,

			// Answers modal
			answersParticipant: null,
			participantAnswers: [],
		};
	},

	computed: {
		alreadyAssignedIds() {
			return this.participants.map(p => p.user._id);
		},

		markedCount() {
			return Object.values(this.attendanceDraft).filter(v => v === true || v === false).length;
		},

		groupedAnswers() {
			if (!this.classroom) return [];
			const groups = [];
			for (const chapter of this.classroom.chapters) {
				const answers = this.participantAnswers.filter(a => a.chapterId === chapter.chapterId);
				if (answers.length > 0)
					groups.push({ chapterId: chapter.chapterId, chapterName: chapter.name, answers });
			}
			return groups;
		},
	},

	methods: {
		async fetchClassroom() {
			try {
				const response = await HTTP.get(`/virtual-classrooms/${this.$route.params.id}`);
				this.classroom = response.data.classroom;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
				this.$router.push("/virtual-classrooms");
			}
		},

		async fetchParticipants() {
			try {
				const response = await HTTP.get(`/virtual-classrooms/${this.$route.params.id}/participants`);
				this.participants = response.data.participants;
			} catch (error) {
				console.error("Erreur API:", error);
			}
		},

		// ─── Overview ───────────────────────────────────────────────
		async toggleChapterLock(chapter) {
			const confirmMessage = chapter.isUnlocked
				? this.$t("virtualClassrooms.detail.confirmLock")
				: this.$t("virtualClassrooms.detail.confirmUnlock");
			if (!confirm(confirmMessage)) return;

			this.isTogglingLock = true;
			try {
				const action = chapter.isUnlocked ? "lock" : "unlock";
				await HTTP.post(`/virtual-classrooms/${this.classroom._id}/chapters/${chapter.chapterId}/${action}`);
				await this.fetchClassroom();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isTogglingLock = false;
			}
		},

		copyToClipboard(text, key) {
			navigator.clipboard.writeText(text).then(() => {
				this.copiedKey = key;
				setTimeout(() => {
					if (this.copiedKey === key) this.copiedKey = null;
				}, 2000);
			});
		},

		// ─── Participants ───────────────────────────────────────────
		openAssignModal() {
			this.employeeSearch = "";
			this.selectedEmployeeIds = [];
			this.availableEmployees = [];

			this.fetchEmployees();

			this.$nextTick(() => {
				const modalElement = document.getElementById("addParticipantsModal");
				let modal = window.bootstrap?.Modal?.getInstance(modalElement);
				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}
				modal.show();
			});
		},

		async fetchEmployees() {
			try {
				const params = {
					page: 1,
					limit: 30,
					search: this.employeeSearch || undefined,
				};
				const response = await HTTP.get("/company/employees", { params });
				this.availableEmployees = response.data.employees;
			} catch (error) {
				console.error("Erreur API:", error);
			}
		},

		searchEmployeesDebounced() {
			if (this.searchTimeout) clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.fetchEmployees();
			}, 300);
		},

		async addParticipants() {
			if (this.selectedEmployeeIds.length === 0) return;

			this.isAddingParticipants = true;
			try {
				const response = await HTTP.post(`/virtual-classrooms/${this.classroom._id}/participants`, {
					employeeIds: this.selectedEmployeeIds,
				});

				const modal = window.bootstrap.Modal.getInstance(document.getElementById("addParticipantsModal"));
				if (modal) modal.hide();

				await this.fetchParticipants();

				const { skippedEmails } = response.data;
				if (skippedEmails && skippedEmails.length > 0) {
					alert(this.$t("virtualClassrooms.participants.skippedCount", { count: skippedEmails.length }));
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isAddingParticipants = false;
			}
		},

		async resendInvite(participant) {
			try {
				await HTTP.post(`/virtual-classrooms/${this.classroom._id}/participants/${participant._id}/resend-invite`);
				this.invitedKey = participant._id;
				setTimeout(() => {
					if (this.invitedKey === participant._id) this.invitedKey = null;
				}, 2000);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async removeParticipant(participant) {
			if (!confirm(this.$t("virtualClassrooms.participants.confirmRemove"))) return;

			try {
				await HTTP.delete(`/virtual-classrooms/${this.classroom._id}/participants/${participant._id}`);
				await this.fetchParticipants();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		// ─── Attendance ─────────────────────────────────────────────
		openAttendanceTab() {
			this.activeTab = "attendance";
			if (!this.selectedDayId && this.classroom.days.length > 0) {
				const todayDay = this.classroom.days.find(d => d.date === this.todayStr());
				this.selectAttendanceDay(todayDay ? todayDay.dayId : this.classroom.days[0].dayId);
			} else if (this.selectedDayId) {
				this.buildAttendanceDraft();
			}
		},

		selectAttendanceDay(dayId) {
			this.selectedDayId = dayId;
			this.attendanceSaved = false;
			this.buildAttendanceDraft();
		},

		buildAttendanceDraft() {
			const draft = {};
			for (const participant of this.participants) {
				const record = (participant.attendance || []).find(a => a.dayId === this.selectedDayId);
				draft[participant._id] = record ? record.present : null;
			}
			this.attendanceDraft = draft;
		},

		setAttendance(participantId, present) {
			this.attendanceDraft[participantId] = present;
			this.attendanceSaved = false;
		},

		async saveAttendance() {
			const records = Object.entries(this.attendanceDraft)
				.filter(([, present]) => present === true || present === false)
				.map(([participantId, present]) => ({ participantId, present }));

			if (records.length === 0) return;

			this.isSavingAttendance = true;
			try {
				await HTTP.put(`/virtual-classrooms/${this.classroom._id}/attendance`, {
					dayId: this.selectedDayId,
					records,
				});
				this.attendanceSaved = true;
				await this.fetchParticipants();
				this.buildAttendanceDraft();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isSavingAttendance = false;
			}
		},

		// ─── Results ────────────────────────────────────────────────
		chapterProgress(participant, chapterId) {
			return (participant.chapterProgress || []).find(p => p.chapterId === chapterId);
		},

		progressLabel(participant, chapterId) {
			const progress = this.chapterProgress(participant, chapterId);
			if (!progress || progress.status === "pending") return this.$t("virtualClassrooms.results.pending");
			if (progress.status === "started") return this.$t("virtualClassrooms.results.started");
			return `${progress.score}%`;
		},

		progressBadgeClass(participant, chapterId) {
			const progress = this.chapterProgress(participant, chapterId);
			if (!progress || progress.status === "pending") return "bg-label-secondary";
			if (progress.status === "started") return "bg-label-info";
			if (progress.score >= 70) return "bg-label-success";
			if (progress.score >= 40) return "bg-label-warning";
			return "bg-label-danger";
		},

		async openAnswersModal(participant) {
			this.answersParticipant = participant;
			this.participantAnswers = [];

			try {
				const response = await HTTP.get(`/virtual-classrooms/${this.classroom._id}/participants/${participant._id}/answers`);
				this.participantAnswers = response.data.answers;
			} catch (error) {
				console.error("Erreur API:", error);
			}

			this.$nextTick(() => {
				const modalElement = document.getElementById("participantAnswersModal");
				let modal = window.bootstrap?.Modal?.getInstance(modalElement);
				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}
				modal.show();
			});
		},

		// ─── Helpers ────────────────────────────────────────────────
		initials(user) {
			if (!user) return "?";
			return `${(user.firstName || "")[0] || ""}${(user.lastName || "")[0] || ""}`.toUpperCase();
		},

		formatDate(dateStr) {
			if (!dateStr) return "-";
			return dateStr.split("-").reverse().join("/");
		},

		chapterDayLabel(chapter) {
			if (!chapter.dayId) return null;
			const index = this.classroom.days.findIndex(d => d.dayId === chapter.dayId);
			if (index === -1) return null;
			return `${this.$t("virtualClassrooms.form.dayLabel")} ${index + 1}`;
		},

		todayStr() {
			const now = new Date();
			return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
		},

		isToday(dateStr) {
			return dateStr === this.todayStr();
		},

		truncate(str, maxLen) {
			if (!str) return "";
			return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
		},
	},

	async mounted() {
		await this.fetchClassroom();
		await this.fetchParticipants();
	},
};
</script>
